// pages/GuildBoard.js (완성된 길드 게시판)
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, getDocs, deleteDoc, doc, query, where, orderBy, 
  serverTimestamp, updateDoc, getDoc
} from 'firebase/firestore';

function GuildBoard({ guildId, currentUser, localUser, onMessage, setMsg }) {
  // props 호환성 처리
  const user = currentUser || localUser;
  const messageHandler = onMessage || setMsg;

  // 게시판 상태
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 게시글 작성 상태
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [newPost, setNewPost] = useState({ 
    title: '', 
    content: '', 
    isNotice: false 
  });
  
  // 게시글 상세 보기 상태
  const [selectedPost, setSelectedPost] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // 댓글 상태
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  // 길드 정보 상태
  const [guild, setGuild] = useState(null);

  // 게시판 종류 탭 상태
  const [boardType, setBoardType] = useState('notice'); // 'notice', 'chat', 'match'

  // 부길드장 권한 예시(실제 데이터 구조에 맞게 수정 필요)
  const isSubLeader = guild?.subLeaderIds?.includes(user?.id);
  const isLeader = guild?.guildMasterId === user?.id;
  const isGuildMember = guild?.memberIds?.includes(user?.id) || isLeader || isSubLeader;

  // 게시글 목록 로드 (boardType별로)
  const fetchPosts = useCallback(async () => {
    if (!guildId) return;
    try {
      const postsQuery = query(
        collection(db, 'guildPosts'),
        where('guildId', '==', guildId),
        orderBy('createdAt', 'desc')
      );
      const postsSnap = await getDocs(postsQuery);
      let postsData = postsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      // type이 없는 글은 notice 탭에서만 보이게
      postsData = postsData.filter(post => {
        if (post.type) return post.type === boardType;
        return boardType === 'notice';
      });
      setPosts(postsData);
    } catch (error) {
      console.error('게시글 로드 실패:', error);
      if (messageHandler) messageHandler('게시글을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [guildId, messageHandler, boardType]);

  // 댓글 로드
  const fetchComments = useCallback(async (postId) => {
    try {
      const commentsQuery = query(
        collection(db, 'guildPostComments'),
        where('postId', '==', postId),
        orderBy('createdAt', 'asc')
      );
      const commentsSnap = await getDocs(commentsQuery);
      
      const commentsData = commentsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setComments(commentsData);
    } catch (error) {
      console.error('댓글 로드 실패:', error);
    }
  }, []);

  // 길드 정보 로드
  useEffect(() => {
    if (!guildId) return;
    const fetchGuild = async () => {
      try {
        const guildRef = doc(db, 'guilds', guildId);
        const guildSnap = await getDoc(guildRef);
        if (guildSnap.exists()) {
          setGuild({ id: guildSnap.id, ...guildSnap.data() });
        }
      } catch (error) {
        console.error('길드 정보 로드 실패:', error);
      }
    };
    fetchGuild();
  }, [guildId]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // 게시글 작성 (type 추가)
  const createPost = async () => {
    if (!user || !user.id) {
      if (messageHandler) messageHandler('로그인이 필요합니다.');
      return;
    }
    if (!newPost.title.trim() || !newPost.content.trim()) {
      if (messageHandler) messageHandler('제목과 내용을 모두 입력해주세요.');
      return;
    }
    try {
      await addDoc(collection(db, 'guildPosts'), {
        guildId: guildId,
        authorId: user.id,
        authorNickname: user.nickname || user.id,
        title: newPost.title,
        content: newPost.content,
        isNotice: newPost.isNotice,
        type: boardType, // 게시판 종류 저장
        createdAt: serverTimestamp(),
        views: 0,
        likes: 0
      });
      setNewPost({ title: '', content: '', isNotice: false });
      setPostModalOpen(false);
      fetchPosts();
      if (messageHandler) messageHandler('게시글이 작성되었습니다.');
    } catch (error) {
      console.error('게시글 작성 실패:', error);
      if (messageHandler) messageHandler('게시글 작성에 실패했습니다.');
    }
  };

  // 게시글 삭제
  const deletePost = async (postId, authorId) => {
    if (authorId !== user.id) {
      if (messageHandler) messageHandler('자신의 게시글만 삭제할 수 있습니다.');
      return;
    }

    if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'guildPosts', postId));
        setPosts(prev => prev.filter(post => post.id !== postId));
        setDetailModalOpen(false);
        if (messageHandler) messageHandler('게시글이 삭제되었습니다.');
      } catch (error) {
        console.error('게시글 삭제 실패:', error);
        if (messageHandler) messageHandler('게시글 삭제에 실패했습니다.');
      }
    }
  };

  // 조회수 증가
  const incrementViews = async (postId) => {
    try {
      const postRef = doc(db, 'guildPosts', postId);
      const post = posts.find(p => p.id === postId);
      if (post) {
        await updateDoc(postRef, {
          views: (post.views || 0) + 1
        });
        
        setPosts(prev => prev.map(p => 
          p.id === postId ? { ...p, views: (p.views || 0) + 1 } : p
        ));
      }
    } catch (error) {
      console.error('조회수 업데이트 실패:', error);
    }
  };

  // 게시글 상세 보기
  const openPostDetail = (post) => {
    setSelectedPost(post);
    setDetailModalOpen(true);
    incrementViews(post.id);
    fetchComments(post.id);
  };
  
  // 댓글 작성
  const addComment = async () => {
    if (!newComment.trim()) {
      if (messageHandler) messageHandler('댓글 내용을 입력해주세요.');
      return;
    }
    
    try {
      await addDoc(collection(db, 'guildPostComments'), {
        postId: selectedPost.id,
        guildId: guildId,
        authorId: user.id,
        authorNickname: user.nickname || user.id,
        content: newComment,
        createdAt: serverTimestamp()
      });
      
      setNewComment('');
      fetchComments(selectedPost.id);
      if (messageHandler) messageHandler('댓글이 작성되었습니다.');
    } catch (error) {
      console.error('댓글 작성 실패:', error);
      if (messageHandler) messageHandler('댓글 작성에 실패했습니다.');
    }
  };

  // 댓글 삭제
  const deleteComment = async (commentId, authorId) => {
    if (authorId !== user.id) {
      if (messageHandler) messageHandler('자신의 댓글만 삭제할 수 있습니다.');
      return;
    }

    if (window.confirm('정말로 이 댓글을 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'guildPostComments', commentId));
        setComments(prev => prev.filter(comment => comment.id !== commentId));
        if (messageHandler) messageHandler('댓글이 삭제되었습니다.');
      } catch (error) {
        console.error('댓글 삭제 실패:', error);
        if (messageHandler) messageHandler('댓글 삭제에 실패했습니다.');
      }
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
        fontSize: 16,
        color: '#6b7280'
      }}>
        <div>📝 게시글 로딩중...</div>
      </div>
    );
  }

  return (
    <div>
      {/* 게시판 탭 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button onClick={() => setBoardType('notice')} style={{ fontWeight: boardType==='notice'?800:500, color: boardType==='notice'?'#6366f1':'#222', background: boardType==='notice'?'#eef2ff':'#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 16, cursor: 'pointer' }}>공지</button>
        <button onClick={() => setBoardType('chat')} style={{ fontWeight: boardType==='chat'?800:500, color: boardType==='chat'?'#6366f1':'#222', background: boardType==='chat'?'#eef2ff':'#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 16, cursor: 'pointer' }}>잡담</button>
        <button onClick={() => setBoardType('match')} style={{ fontWeight: boardType==='match'?800:500, color: boardType==='match'?'#6366f1':'#222', background: boardType==='match'?'#eef2ff':'#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 16, cursor: 'pointer' }}>매치</button>
      </div>
      {/* 게시판 헤더 */}
      <div style={{
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'black'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: 0,
            color: 'black'
          }}>
            📝 {boardType==='notice'?'공지':boardType==='chat'?'잡담':'매치'} 게시판
          </h3>
          {/* 글쓰기 버튼 권한 분기 */}
          {user && (
            <button
              onClick={() => setPostModalOpen(true)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
              }}
            >
              ✏️ 글 작성
            </button>
          )}
        </div>

        {/* 게시글 목록 */}
        {posts.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {posts.map(post => (
              <div 
                key={post.id} 
                style={{
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  padding: '20px',
                  background: post.isNotice ? 
                    'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => openPostDetail(post)}
              >
                {/* 공지사항 배지 */}
                {post.isNotice && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: '#f59e0b',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    📢 공지
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    margin: 0,
                    color: 'black',
                    flex: 1,
                    marginRight: '16px'
                  }}>
                    {post.title}
                  </h4>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                    <span>👁️ {post.views || 0}</span>
                    <span>❤️ {post.likes || 0}</span>
                    <span>{post.createdAt.toLocaleDateString()}</span>
                  </div>
                </div>
                
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.8)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {post.content}
                </p>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)'
                }}>
                  <span>
                    작성자: <strong style={{ color: 'black' }}>{post.authorNickname}</strong>
                  </span>
                  <span>
                    {post.createdAt.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            color: 'rgba(31,41,55,0.7)',
            fontSize: '16px',
            padding: '60px 0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <div>아직 게시글이 없습니다.</div>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>
              첫 번째 게시글을 작성해보세요!
            </div>
          </div>
        )}
      </div>

      {/* 게시글 작성 모달 */}
      {postModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '600px',
            margin: '20px',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '24px',
              color: '#1f2937',
              textAlign: 'center'
            }}>
              ✏️ 새 게시글 작성
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#1f2937'
              }}>
                제목
              </label>
              <input
                type="text"
                value={newPost.title}
                onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                placeholder="게시글 제목을 입력하세요"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#1f2937'
              }}>
                내용
              </label>
              <textarea
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                placeholder="게시글 내용을 입력하세요"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  minHeight: '150px',
                  resize: 'vertical',
                  lineHeight: '1.5',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setPostModalOpen(false);
                  setNewPost({ title: '', content: '' });
                }}
                style={{
                  padding: '12px 24px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                취소
              </button>
              <button
                onClick={async () => { await createPost(); fetchPosts(); }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                작성하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 게시글 상세 보기 모달 */}
      {detailModalOpen && selectedPost && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '800px',
            margin: '20px',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            {/* 게시글 헤더 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px'
            }}>
              <div style={{ flex: 1 }}>
                {selectedPost.isNotice && (
                  <div style={{
                    background: '#f59e0b',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    display: 'inline-block'
                  }}>
                    📢 공지사항
                  </div>
                )}
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: '0 0 8px 0',
                  color: '#1f2937'
                }}>
                  {selectedPost.title}
                </h2>
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span>작성자: {selectedPost.authorNickname}</span>
                  <span>작성일: {selectedPost.createdAt.toLocaleString()}</span>
                  <span>조회수: {selectedPost.views || 0}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedPost.authorId === user.id && (
                  <button
                    onClick={() => deletePost(selectedPost.id, selectedPost.authorId)}
                    style={{
                      padding: '8px 12px',
                      background: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    삭제
                  </button>
                )}
                <button
                  onClick={() => setDetailModalOpen(false)}
                  style={{
                    padding: '8px 12px',
                    background: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  닫기
                </button>
              </div>
            </div>

            {/* 게시글 내용 */}
            <div style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#1f2937',
              marginBottom: '32px',
              padding: '24px',
              background: '#f8fafc',
              borderRadius: '12px',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedPost.content}
            </div>

            {/* 댓글 섹션 */}
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#1f2937'
              }}>
                💬 댓글 ({comments.length})
              </h3>

              {/* 댓글 작성 */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="댓글을 입력하세요"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addComment();
                    }
                  }}
                />
                <button
                  onClick={addComment}
                  style={{
                    padding: '12px 20px',
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  댓글 작성
                </button>
              </div>

              {/* 댓글 목록 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {comments.map(comment => (
                  <div
                    key={comment.id}
                    style={{
                      padding: '16px',
                      background: '#f1f5f9',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          fontWeight: 'bold',
                          color: '#1f2937'
                        }}>
                          {comment.authorNickname}
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          {comment.createdAt.toLocaleString()}
                        </span>
                      </div>
                      {comment.authorId === user.id && (
                        <button
                          onClick={() => deleteComment(comment.id, comment.authorId)}
                          style={{
                            padding: '4px 8px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          삭제
                        </button>
                      )}
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: '1.5'
                    }}>
                      {comment.content}
                    </p>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px',
                    padding: '24px'
                  }}>
                    첫 번째 댓글을 작성해보세요!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuildBoard;